import { useEffect } from "react";
import type { SessionRow } from "../openclaw/types";
import { useT } from "../lib/useT";
import { conversationTitle, useStore } from "../state/store";
import { Sheet } from "./Sheet";

/**
 * Picks the model for one conversation.
 *
 * Per conversation rather than globally, because that is how the Gateway stores
 * it (`sessions.patch.model`) and how it is actually used: the quick chat and
 * the one doing real work want different models. "Standard" clears the pin and
 * hands the session back to whatever the agent is configured with.
 */
export function ModelPicker({ row, onClose }: { row: SessionRow; onClose: () => void }) {
  const models = useStore((state) => state.models);
  const loading = useStore((state) => state.modelsLoading);
  const loadModels = useStore((state) => state.loadModels);
  const setModel = useStore((state) => state.setModel);
  const t = useT();

  useEffect(() => {
    void loadModels();
  }, [loadModels]);

  const choose = (model: string | null) => {
    void setModel(row.key, model);
    onClose();
  };

  return (
    <Sheet
      title={t("model.title")}
      description={t("model.description", { title: conversationTitle(row) })}
      onClose={onClose}
    >
      <div className="model-list">
        <button
          type="button"
          className={`model-option${row.model ? "" : " is-active"}`}
          onClick={() => choose(null)}
        >
          <span className="model-option-body">
            <span className="model-option-name">{t("model.agentDefault")}</span>
            <span className="model-option-meta">{t("model.agentDefaultMeta")}</span>
          </span>
        </button>

        {loading && !models.length ? (
          <div className="model-empty">
            <span className="spinner" />
            <span>{t("model.loading")}</span>
          </div>
        ) : null}

        {!loading && !models.length ? (
          <div className="model-empty">
            {t("model.none")}
          </div>
        ) : null}

        {models.map((model) => {
          const active = row.model === model.id || row.model === model.alias;
          return (
            <button
              key={model.id}
              type="button"
              className={`model-option${active ? " is-active" : ""}${
                model.available === false ? " is-unavailable" : ""
              }`}
              onClick={() => choose(model.alias ?? model.id)}
              disabled={model.available === false}
            >
              <span className="model-option-body">
                <span className="model-option-name">{model.name}</span>
                <span className="model-option-meta">
                  {model.provider}
                  {model.contextWindow
                    ? t("model.context", { size: Math.round(model.contextWindow / 1000) })
                    : ""}
                  {model.reasoning ? t("model.reasoning") : ""}
                  {model.available === false ? t("model.unavailable") : ""}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}
